import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Switch, Route, useRouteMatch, useHistory, Prompt, useLocation, Redirect } from 'react-router-dom';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { usePopover } from 'shared/providers/PopoverWindowProvider';
import { PopoverName } from 'shared/enums/PopoverName';
import { Box, useDisclosure, VStack, Flex } from '@televet/kibble-ui';
import { LeavingCurrentPage, useSettingPageDetail } from 'pages/Settings/hooks/useSettingPageDetail';
import UnsavedFormChangesModal, { PageType } from 'pages/Forms/components/UnsavedFormChangesModal';
import { Location } from 'history';
import CollapsibleFooter from 'shared/components/CollapsibleFooter';
import useClinicUser from 'shared/hooks/useClinicUser';
import { PermissionType } from 'shared/types/graphql';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import SettingsNavigation from './components/SettingsNavigation';
import SettingsHeader from './components/SettingsHeader';
import { useModularity } from 'shared/hooks/useModularity';

const Settings = (): JSX.Element => {
  // ref section
  const settingsPageRef = useRef<LeavingCurrentPage>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);

  // custom hooks
  const { allSectionsOptions } = useSettingPageDetail();
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { isTouchDevice, isMobile } = useResolutionProvider();
  const { popover } = usePopover();
  const { userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);
  const { modules } = useModularity();

  // page State
  const [hasUnsavedChange, setHasUnsavedChange] = useState<boolean>(false);
  const [isSettingsChangeValid, setIsSettingsChangesValid] = useState<boolean>(true);
  const [lastLocation, setLastLocation] = useState<Location>(location);
  const [onNavChangeConfirmed, setOnNavChangeConfirmed] = useState(false);

  const [, setHasUnsavedChangePersisted] = usePersistedState(
    PersistedStateKeys.HasUnsavedSettingsChanges,
    hasUnsavedChange,
  );

  // modal hooks
  const {
    isOpen: isUnsavedChangesOpen,
    onOpen: onUnsavedChangesOpen,
    onClose: onUnsavedChangesClose,
  } = useDisclosure();

  // handler function
  const handleBlockedNavigation = (location: Location): boolean => {
    if (hasUnsavedChange) {
      onUnsavedChangesOpen();
      setLastLocation(location);
      return false;
    }
    return true;
  };

  const onLeaveCurrentPage = (): void => {
    setOnNavChangeConfirmed(true);
    setHasUnsavedChange(false);
    settingsPageRef.current?.onLeavingCurrentPage();
    history.push(lastLocation?.pathname);
  };

  useEffect(() => {
    const handleBrowserClosed = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      return (event.returnValue = 'Are you sure you want to exit?');
    };

    {
      hasUnsavedChange && window.addEventListener('beforeunload', handleBrowserClosed);
    }

    return (): void => {
      window.removeEventListener('beforeunload', handleBrowserClosed);
    };
  }, [hasUnsavedChange]);

  useEffect(() => {
    if (onNavChangeConfirmed && lastLocation) {
      history.push(lastLocation?.pathname);
      onUnsavedChangesClose();
      setOnNavChangeConfirmed(false);
    }
  }, [onNavChangeConfirmed, lastLocation, history, onUnsavedChangesClose]);

  useEffect(() => {
    setHasUnsavedChangePersisted(hasUnsavedChange);
    setIsSettingsChangesValid(isSettingsChangeValid);
    if (!hasUnsavedChange) {
      onUnsavedChangesClose();
    }
  }, [hasUnsavedChange, onUnsavedChangesClose, setHasUnsavedChangePersisted, isSettingsChangeValid]);

  const visiblePages = useMemo(() => allSectionsOptions.filter((page) => page.isVisible), [allSectionsOptions]);

  return (
    <Flex className="Settings" w="100%" h="100%">
      {!isMobile && <SettingsNavigation isMobile={isMobile} />}
      <Switch>
        {isMobile ? (
          <Route path={`${path}${SettingsRoutes.Index}`} exact>
            <Flex direction="column" w="100%" h="100%">
              <SettingsHeader relativePath={SettingsRoutes.Index} isMobile={isMobile} />
              <SettingsNavigation isMobile={isMobile} />
            </Flex>
          </Route>
        ) : (
          <Redirect exact from={`${path}${SettingsRoutes.Index}`} to={`${path}${SettingsRoutes.UserProfile}`} />
        )}
        {visiblePages.map((page) => (
          <Route path={`${path}${page.relativePath}`} key={page.relativePath}>
            <VStack
              flex="1 1 auto"
              spacing={0}
              w="100%"
              h={
                isTouchDevice && popover?.isOpen && popover?.name === PopoverName.PhoneCall
                  ? 'calc(100% - 64px)'
                  : '100%'
              }
              justifyContent="flex-start"
              alignItems="stretch"
              marginInlineStart="0px !important"
            >
              <Flex direction="column" overflowY="scroll">
                <SettingsHeader relativePath={page.relativePath} isMobile={isMobile} />
                <Box ref={viewRef} overflowY="scroll" m="0px 20px" pb="200px" flex="1 1 auto">
                  <page.component
                    setHasUnsavedChange={setHasUnsavedChange}
                    setIsSettingsChangesValid={setIsSettingsChangesValid}
                    isSettingsChangesValid={isSettingsChangeValid}
                    hasPermission={hasPermission}
                    ref={settingsPageRef}
                    viewRef={viewRef}
                  />
                </Box>
              </Flex>
              <Prompt message={handleBlockedNavigation} />
              <UnsavedFormChangesModal
                pageType={PageType.Setting}
                isOpen={isUnsavedChangesOpen}
                onClose={onUnsavedChangesClose}
                onConfirm={(): void => onLeaveCurrentPage()}
              />
              <CollapsibleFooter
                isLoading={false}
                isDisabled={!isSettingsChangeValid}
                onSave={(): void => {
                  setHasUnsavedChange(false), settingsPageRef.current?.onClickedSaveButton();
                }}
                onCancel={(): void => {
                  setHasUnsavedChange(false), settingsPageRef.current?.onClickedCancelButton();
                }}
                hasChanges={hasUnsavedChange}
              />
            </VStack>
          </Route>
        ))}
        {!!modules.length && <Redirect from="*" to={RouteBasePaths.Settings} />}
      </Switch>
    </Flex>
  );
};

export default Settings;
